import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { EyeIcon, TrashIcon, ArchiveBoxIcon } from '@heroicons/react/20/solid'
import { webLeadsApi } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { SortableHeader, useSort } from '../../components/ui/SortableHeader'

type SortField = 'name' | 'company' | 'email' | 'interest' | 'status' | 'createdAt'

const STATUSES = ['unviewed', 'viewed', 'archived'] as const
type Status = typeof STATUSES[number] | 'all'

const INTERESTS = {
  discovery_day: 'Discovery Day',
  pilot_project: 'Pilot Project',
  partnership_request: 'Partnership Request',
  general_question: 'General Question',
} as const

function WebLeadStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    unviewed: 'bg-red-100 text-red-800',
    viewed: 'bg-blue-100 text-blue-800',
    converted: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-800',
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  )
}

function InterestBadge({ interest }: { interest: string }) {
  const colors: Record<string, string> = {
    discovery_day: 'bg-purple-100 text-purple-800',
    pilot_project: 'bg-indigo-100 text-indigo-800',
    partnership_request: 'bg-amber-100 text-amber-800',
    general_question: 'bg-cyan-100 text-cyan-800',
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[interest] || 'bg-gray-100 text-gray-800'}`}>
      {INTERESTS[interest as keyof typeof INTERESTS] || interest}
    </span>
  )
}

export function WebLeadsListPage() {
  const [statusFilter, setStatusFilter] = useState<Status>('all')
  const [page, setPage] = useState(1)
  const limit = 20
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined

  const { data: response, isLoading } = useQuery({
    queryKey: ['web-leads', { page, limit, filters }],
    queryFn: () => webLeadsApi.list({ page, limit, filters }),
  })

  const { data: stats } = useQuery({
    queryKey: ['web-leads-stats'],
    queryFn: () => webLeadsApi.getStats(),
  })

  const { sortField, sortDirection, handleSort } = useSort<SortField>('createdAt', 'desc')

  const sortedWebLeads = useMemo(() => {
    const webLeads = response?.data || []
    if (!webLeads.length) return []

    const result = [...webLeads]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '')
          break
        case 'company':
          comparison = (a.company || '').localeCompare(b.company || '')
          break
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '')
          break
        case 'interest': {
          const INTEREST_ORDER: Record<string, number> = {
            discovery_day: 0,
            pilot_project: 1,
            partnership_request: 2,
            general_question: 3,
          }
          comparison = (INTEREST_ORDER[a.interest] ?? 99) - (INTEREST_ORDER[b.interest] ?? 99)
          break
        }
        case 'status': {
          const STATUS_ORDER: Record<string, number> = {
            unviewed: 0,
            viewed: 1,
            converted: 2,
            archived: 3,
          }
          comparison = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99)
          break
        }
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [response?.data, sortField, sortDirection])

  const archiveMutation = useMutation({
    mutationFn: webLeadsApi.archive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['web-leads'] })
      queryClient.invalidateQueries({ queryKey: ['web-leads-stats'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: webLeadsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['web-leads'] })
      queryClient.invalidateQueries({ queryKey: ['web-leads-stats'] })
    },
  })

  const handleStatusFilter = (status: Status) => {
    setStatusFilter(status)
    setPage(1)
  }

  if (isLoading) return <div>{t('common.loading')}</div>

  const total = response?.meta?.total || 0
  const totalPages = Math.ceil(total / limit)

  return (
    <>
      {/* Secondary column */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('pages.webLeads.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('pages.webLeads.description')}
            </p>
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} {t('fields.status')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {STATUSES.map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status}
                  {status === 'unviewed' && stats?.unviewedCount ? (
                    <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {stats.unviewedCount}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.webLeads.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.webLeads.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{total}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.webLeads.stats.unviewed')}</dt>
                <dd className="font-medium text-red-600">{stats?.unviewedCount || 0}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('pages.webLeads.title')}</h1>
            {stats?.unviewedCount ? (
              <span className="ml-2 bg-red-500 text-white text-sm px-2 py-1 rounded-full">
                {stats.unviewedCount} {t('pages.webLeads.stats.unviewed')}
              </span>
            ) : null}
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader
                          field="name"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                          className="pl-4 sm:pl-6"
                        >
                          {t('fields.name')}
                        </SortableHeader>
                        <SortableHeader
                          field="company"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        >
                          {t('fields.organisation')}
                        </SortableHeader>
                        <SortableHeader
                          field="email"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        >
                          {t('fields.email')}
                        </SortableHeader>
                        <SortableHeader
                          field="interest"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        >
                          {t('pages.webLeads.fields.interest')}
                        </SortableHeader>
                        <SortableHeader
                          field="status"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        >
                          {t('fields.status')}
                        </SortableHeader>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('pages.webLeads.fields.source')}
                        </th>
                        <SortableHeader
                          field="createdAt"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        >
                          {t('fields.createdAt')}
                        </SortableHeader>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {sortedWebLeads.map((lead) => (
                        <tr key={lead.id} className={`hover:bg-gray-50 transition-colors ${lead.status === 'unviewed' ? 'bg-red-50' : ''}`}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            <div className="flex items-center gap-2">
                              {lead.status === 'unviewed' && (
                                <span className="h-2 w-2 rounded-full bg-red-500" title="Unviewed" />
                              )}
                              {lead.name}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {lead.company}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <a href={`mailto:${lead.email}`} className="text-brand-600 hover:text-brand-900">
                              {lead.email}
                            </a>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <InterestBadge interest={lead.interest} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <WebLeadStatusBadge status={lead.status} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {lead.websiteConfigName || '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {new Date(lead.createdAt).toLocaleDateString()}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/web-leads/${lead.id}`}
                              className="inline-flex items-center gap-x-1 text-brand-600 hover:text-brand-900 mr-3"
                            >
                              <EyeIcon className="h-4 w-4" />
                              {t('common.view')}
                            </Link>
                            {lead.status !== 'archived' && (
                              <button
                                onClick={() => archiveMutation.mutate(lead.id)}
                                className="inline-flex items-center gap-x-1 text-gray-600 hover:text-gray-900 mr-3"
                              >
                                <ArchiveBoxIcon className="h-4 w-4" />
                                {t('common.archive')}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                if (confirm(t('common.confirmDelete'))) {
                                  deleteMutation.mutate(lead.id)
                                }
                              }}
                              className="inline-flex items-center gap-x-1 text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {sortedWebLeads.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500">
                            {t('pages.webLeads.empty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                      <div className="flex flex-1 justify-between sm:hidden">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                          <span className="font-medium">{Math.min(page * limit, total)}</span> of{' '}
                          <span className="font-medium">{total}</span> results
                        </p>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                          <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                            {page} / {totalPages}
                          </span>
                          <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
